"use client";

import { useEffect } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import type { Route } from "@lifi/sdk";
import type { RouteExecutionUpdate, ChainTokenSelected, RouteSelected, ContactSupport } from "@lifi/widget";
import { useWidgetEvents, WidgetEvent } from "@lifi/widget";

/**
 * Extract comprehensive route properties for analytics
 */
function extractRouteProperties(route: Route) {
  // Get the tools/bridges used in the route
  const tools = route.steps?.map((step) => step.tool).filter(Boolean) ?? [];
  const toolNames = route.steps?.map((step) => step.toolDetails?.name).filter(Boolean) ?? [];

  return {
    // Route identification
    routeId: route.id,

    // Chain information
    fromChainId: route.fromChainId,
    toChainId: route.toChainId,

    // Source token details
    fromTokenAddress: route.fromToken?.address,
    fromTokenSymbol: route.fromToken?.symbol,
    fromTokenName: route.fromToken?.name,
    fromTokenDecimals: route.fromToken?.decimals,
    fromTokenPriceUSD: route.fromToken?.priceUSD,

    // Destination token details
    toTokenAddress: route.toToken?.address,
    toTokenSymbol: route.toToken?.symbol,
    toTokenName: route.toToken?.name,
    toTokenDecimals: route.toToken?.decimals,
    toTokenPriceUSD: route.toToken?.priceUSD,

    // Amounts
    fromAmount: route.fromAmount,
    toAmount: route.toAmount,
    toAmountMin: route.toAmountMin,

    // USD values
    fromAmountUSD: route.fromAmountUSD,
    toAmountUSD: route.toAmountUSD,

    // Costs
    gasCostUSD: route.gasCostUSD,

    // Route metadata
    tools: tools.join(","),
    toolNames: toolNames.join(","),
    stepsCount: route.steps?.length ?? 0,

    // Tags (if available)
    tags: route.tags?.join(","),
  };
}

/**
 * Hook to track LI.FI widget events for analytics
 * Subscribes to all widget events and forwards them to Mixpanel
 *
 * IMPORTANT: This hook must be used OUTSIDE the LiFiWidget component
 * to optimize performance (as per LI.FI documentation)
 */
export function useBridgeEvents() {
  const widgetEvents = useWidgetEvents();
  const { trackEvent } = useMetricsEvents();

  useEffect(() => {
    // Route execution started - user initiated a bridge transaction
    const handleRouteExecutionStarted = (route: Route) => {
      trackEvent(METRICS_EVENTS.BRIDGE_ROUTE_EXECUTION_STARTED, extractRouteProperties(route));
    };

    // Route execution updated - status change during execution
    const handleRouteExecutionUpdated = (update: RouteExecutionUpdate) => {
      trackEvent(METRICS_EVENTS.BRIDGE_ROUTE_EXECUTION_UPDATED, {
        routeId: update.route.id,
        fromChainId: update.route.fromChainId,
        toChainId: update.route.toChainId,
        fromTokenSymbol: update.route.fromToken?.symbol,
        toTokenSymbol: update.route.toToken?.symbol,
        status: update.process?.status,
        substatus: update.process?.substatus,
        txHash: update.process?.txHash,
        txLink: update.process?.txLink,
      });
    };

    // Route execution completed - bridge transaction succeeded
    const handleRouteExecutionCompleted = (route: Route) => {
      trackEvent(METRICS_EVENTS.BRIDGE_ROUTE_EXECUTION_COMPLETED, extractRouteProperties(route));
    };

    // Route execution failed - bridge transaction failed
    const handleRouteExecutionFailed = (update: RouteExecutionUpdate) => {
      trackEvent(METRICS_EVENTS.BRIDGE_ROUTE_EXECUTION_FAILED, {
        routeId: update.route.id,
        fromChainId: update.route.fromChainId,
        toChainId: update.route.toChainId,
        fromTokenSymbol: update.route.fromToken?.symbol,
        toTokenSymbol: update.route.toToken?.symbol,
        fromAmount: update.route.fromAmount,
        fromAmountUSD: update.route.fromAmountUSD,
        errorMessage: update.process?.error?.message,
        errorCode: update.process?.error?.code,
        status: update.process?.status,
        substatus: update.process?.substatus,
      });
    };

    // Route selected - user selected a specific route from available options
    const handleRouteSelected = (data: RouteSelected) => {
      trackEvent(METRICS_EVENTS.BRIDGE_ROUTE_SELECTED, {
        ...extractRouteProperties(data.route),
        totalRoutesAvailable: data.routes.length,
      });
    };

    // Send to wallet toggled - user toggled sending to a different address
    const handleSendToWalletToggled = (enabled: boolean) => {
      trackEvent(METRICS_EVENTS.BRIDGE_SEND_TO_WALLET_TOGGLED, {
        enabled,
      });
    };

    // Contact support clicked
    const handleContactSupportRequested = (data: ContactSupport) => {
      trackEvent(METRICS_EVENTS.BRIDGE_CONTACT_SUPPORT_CLICKED, {
        supportId: data.supportId,
      });
    };

    // Source token selected - user selected a source chain/token
    const handleSourceChainTokenSelected = (token: ChainTokenSelected) => {
      trackEvent(METRICS_EVENTS.BRIDGE_SOURCE_TOKEN_SELECTED, {
        chainId: token.chainId,
        tokenAddress: token.tokenAddress,
      });
    };

    // Destination token selected - user selected a destination chain/token
    const handleDestinationChainTokenSelected = (token: ChainTokenSelected) => {
      trackEvent(METRICS_EVENTS.BRIDGE_DESTINATION_TOKEN_SELECTED, {
        chainId: token.chainId,
        tokenAddress: token.tokenAddress,
      });
    };

    // Subscribe to all events
    widgetEvents.on(WidgetEvent.RouteExecutionStarted, handleRouteExecutionStarted);
    widgetEvents.on(WidgetEvent.RouteExecutionUpdated, handleRouteExecutionUpdated);
    widgetEvents.on(WidgetEvent.RouteExecutionCompleted, handleRouteExecutionCompleted);
    widgetEvents.on(WidgetEvent.RouteExecutionFailed, handleRouteExecutionFailed);
    widgetEvents.on(WidgetEvent.RouteSelected, handleRouteSelected);
    widgetEvents.on(WidgetEvent.SendToWalletToggled, handleSendToWalletToggled);
    widgetEvents.on(WidgetEvent.ContactSupport, handleContactSupportRequested);
    widgetEvents.on(WidgetEvent.SourceChainTokenSelected, handleSourceChainTokenSelected);
    widgetEvents.on(WidgetEvent.DestinationChainTokenSelected, handleDestinationChainTokenSelected);

    // Cleanup - clear all event handlers
    return () => {
      widgetEvents.all.clear();
    };
  }, [widgetEvents, trackEvent]);
}
